/*  global_dpm_rebalance.ts
    – Pure Global DPM rebalance engine. No firebase imports (same rule as
      global_dpm_matching.ts) so it is unit-tested with vitest and reusable by
      functions later.

    Flow: valuation (portfolioValueUsd) → drift per model line → sells in units,
    buys in USD (computeBatch, the draft — buys are an estimate from planned
    proceeds) → once a portfolio's sell rows are allocated by their trades, its
    buys are sized from the live position's cash (computeBuyLeg). KFHC
    house-fund lines become instructions, never allocation rows.

    Geneva rounds fund quantities to whole units in the daily position report
    but not the cost figures, so trueQty recovers total_cost / average_cost for
    fractional (AllFunds) holdings. Drop trueQty once the report is fixed.
*/
import { Fund, GlobalDpmAllocationRow, GlobalDpmBatch, GlobalDpmBatchLine, GlobalDpmBatchPortfolio, GlobalDpmModel, GlobalDpmModelLine, Position, PositionDetails, RiskLevel, stripNoise } from './types';
import { splitBasis, toDdMmYyyy } from './global_dpm_matching';

export const STALE_POSITION_DAYS = 7;

export const fmtPct = (n: number | undefined) => `${(n ?? 0).toFixed(2)}%`;
export const fmtUsd = (n: number | undefined) => (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
export const fmtQty = (n: number | undefined) => (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 6 });
export const BATCH_DEFAULTS = { thresholdPct: 1, minTradeAmount: 0, cashBufferPct: 0.4, minCompletionPct: 90, roundUpPct: 15 };

export type Rates = Record<string, string | number>; // config/exchange_rates.rates — KWD per unit of currency, values stored as strings

const rate = (rates: Rates, ccy: string): number => (ccy === 'KWD' ? 1 : parseFloat(String(rates[ccy] ?? '')) || 0);

/** Any currency → USD through KWD (rates are KWD per unit). Missing rate → 0 so the line is visibly wrong instead of silently KWD. */
export const toUsd = (value: number, ccy: string, rates: Rates): number => {
    const c = (ccy || 'KWD').toUpperCase();
    if (c === 'USD') return value;
    const usd = rate(rates, 'USD');
    return usd > 0 ? (value * rate(rates, c)) / usd : 0;
};

/** True holding of a rounded report row: cost/avg-cost when the reported quantity is whole and cost is populated. */
export const trueQty = (row: Pick<Position, 'quantity' | 'total_cost' | 'average_cost'>): { qty: number; recovered: boolean; flagged: boolean } => {
    const q = row.quantity ?? 0;
    if (!Number.isInteger(q)) return { qty: q, recovered: false, flagged: false };
    const avg = row.average_cost ?? 0;
    const cost = row.total_cost ?? 0;
    if (avg > 0 && cost > 0) return { qty: stripNoise(cost / avg), recovered: true, flagged: false };
    return { qty: q, recovered: false, flagged: q > 0 };
};

export const positionRows = (p?: PositionDetails): Position[] => [...(p?.local_position ?? []), ...(p?.foreign_position ?? [])];

// ponytail: rows without a currency are KWD (the repo-wide resolveCurrency convention); verify on a prod DPM_GLOBAL position
export const rowCurrency = (r: Position): string => (r.currency || (r.cash ? r.code : '') || 'KWD').toUpperCase();

export interface HoldingLine {
    code: string;
    name: string;
    ccy: string;
    reportedQty: number;
    qty: number; // true quantity (see trueQty); kfhc lines use reportedQty
    recovered: boolean;
    flagged: boolean;
    mvUsd: number;
    priceUsd: number; // mvUsd / qty — display and sell sizing only
}

export interface Valuation {
    totalUsd: number;
    cashUsd: number;
    holdings: HoldingLine[];
}

/** Σ non-cash rows (native ccy → USD) + cash rows (quantity is the amount, ccy = currency ?? code). */
export const portfolioValueUsd = (position: PositionDetails | undefined, rates: Rates, wholeUnitCodes: Set<string> = new Set()): Valuation => {
    let cashUsd = 0;
    const holdings: HoldingLine[] = [];
    for (const r of positionRows(position)) {
        const ccy = rowCurrency(r);
        if (r.cash) {
            cashUsd += toUsd(r.quantity ?? 0, ccy, rates);
            continue;
        }
        const code = String(r.code ?? r.firebase_key ?? '');
        const t = wholeUnitCodes.has(code) ? { qty: r.quantity ?? 0, recovered: false, flagged: false } : trueQty(r);
        const mvUsd = toUsd(r.total_market_value ?? 0, ccy, rates);
        holdings.push({ code, name: r.name ?? code, ccy, reportedQty: r.quantity ?? 0, ...t, mvUsd, priceUsd: t.qty > 0 ? mvUsd / t.qty : 0 });
    }
    return { totalUsd: holdings.reduce((s, h) => s + h.mvUsd, 0) + cashUsd, cashUsd, holdings };
};

export const lineForCode = (code: string, lines: GlobalDpmModelLine[]): GlobalDpmModelLine | undefined =>
    lines.find((l) => l.key === code || (!!l.securityNumber && l.securityNumber === code));

/** How much of the level's target weight the portfolio actually holds — a portfolio still buying its initial allocation is "incomplete". */
export const completion = (v: Valuation, levelLines: GlobalDpmModelLine[]): { held: number; total: number; heldPct: number; cashPct: number } => {
    const targeted = levelLines.filter((l) => l.targetPercent > 0);
    const held = targeted.filter((l) => v.holdings.some((h) => h.mvUsd > 0 && (h.code === l.key || (!!l.securityNumber && h.code === l.securityNumber))));
    return { held: held.length, total: targeted.length, heldPct: held.reduce((s, l) => s + l.targetPercent, 0), cashPct: v.totalUsd > 0 ? (v.cashUsd / v.totalUsd) * 100 : 0 };
};

/* ---------- model ---------- */

export const validateModel = (riskLevels: Record<string, GlobalDpmModelLine[]>, riskIds: string[]): { errors: string[]; warnings: string[] } => {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (Object.keys(riskLevels).length === 0) errors.push('model has no risk levels');
    for (const [risk, lines] of Object.entries(riskLevels)) {
        if (!riskIds.includes(risk)) errors.push(`${risk}: not a risk level id in config (${riskIds.join(', ')})`);
        const keys = new Set<string>();
        for (const l of lines) {
            if (!l.key) errors.push(`${risk}: line without key`);
            if (keys.has(l.key)) errors.push(`${risk}: duplicate key ${l.key}`);
            keys.add(l.key);
            if (!(l.targetPercent >= 0)) errors.push(`${risk}/${l.key}: target % must be a number ≥ 0`);
            if (l.kind === 'allfunds' && !l.securityNumber) warnings.push(`${risk}/${l.key}: no Security Number — the holding only joins when the position code is the ISIN`);
            if (l.kind === 'kfhc' && !l.fundId) warnings.push(`${risk}/${l.key}: no Fund ID — the fund rules are found only if a fund document has geneva_investment = ${l.key}`);
            if (l.fallbackKey) {
                const fb = lines.find((x) => x.key === l.fallbackKey);
                if (!fb) errors.push(`${risk}/${l.key}: fallback ${l.fallbackKey} is not a line of the same level`);
                else if (fb === l) errors.push(`${risk}/${l.key}: fallback cannot be the line itself`);
            }
        }
        const sum = lines.reduce((s, l) => s + (l.targetPercent || 0), 0);
        if (Math.abs(sum - 100) > 0.01) errors.push(`${risk}: targets sum to ${sum.toFixed(2)}%, not 100%`);
    }
    return { errors, warnings };
};

/** Long-format excel (Risk Level, Key, Kind, Name, Security Number, Target %, Fallback Key, Min Holding Policy, Fund ID, NAV Buffer %) → riskLevels map. */
export const parseModelSheet = (rows: Record<string, unknown>[]): Record<string, GlobalDpmModelLine[]> => {
    const cell = (r: Record<string, unknown>, ...names: string[]) => {
        const k = Object.keys(r).find((h) => names.includes(h.trim().toLowerCase()));
        return k === undefined ? '' : String(r[k] ?? '').trim();
    };
    const out: Record<string, GlobalDpmModelLine[]> = {};
    for (const r of rows) {
        const risk = cell(r, 'risk level', 'risk', 'risk_level').toLowerCase();
        const key = cell(r, 'key', 'isin', 'isin number');
        if (!risk && !key) continue; // blank row
        const kind = cell(r, 'kind').toLowerCase() === 'kfhc' ? 'kfhc' : 'allfunds';
        const line: GlobalDpmModelLine = {
            key,
            kind,
            name: cell(r, 'name', 'fund name'),
            targetPercent: Number(cell(r, 'target %', 'target', 'target percent').replace('%', '')) || 0,
        };
        const sec = cell(r, 'security number', 'securitynumber');
        if (sec) line.securityNumber = sec;
        const fb = cell(r, 'fallback key', 'fallback');
        if (fb) line.fallbackKey = fb;
        const pol = cell(r, 'min holding policy', 'minholdingpolicy').toLowerCase();
        if (kind === 'kfhc') line.minHoldingPolicy = pol === 'sell_all' ? 'sell_all' : 'hold';
        const fundId = cell(r, 'fund id', 'fundid', 'fund');
        if (kind === 'kfhc' && fundId) line.fundId = fundId;
        const navBuffer = Number(cell(r, 'nav buffer %', 'nav buffer').replace('%', '')) || 0;
        if (kind === 'kfhc' && navBuffer > 0) line.navBufferPct = navBuffer;
        (out[risk] ??= []).push(line);
    }
    return out;
};

export const riskLabel = (riskLevels: RiskLevel[] | undefined, id: string | undefined): string =>
    riskLevels?.find((l) => l.id === id)?.name_en ?? (id || '—');

/* ---------- kfhc fund rules ---------- */

export interface KfhcFundRules {
    key: string; // geneva_investment
    currency: string;
    nav: number; // nav_per_unit
    navDate?: string; // dd-MM-yyyy
    minUnits: number;
    feePct: number;
    overheadPct: number;
    minRedemption: number;
    minHolding: number;
    multipleUnits?: number; // multiples_of_units: subscriptions in whole steps of this many units (sukuk: 1,000)
    navBufferPct?: number; // from the model line, not the fund doc: % added to the last NAV when sizing a subscription
}

/** Rules per model key: the fund named by the line's fundId, else the fund whose geneva_investment is the key; plus the line's NAV buffer. */
export const fundsForModel = (funds: Fund[], model: Pick<GlobalDpmModel, 'riskLevels'> | undefined): Record<string, KfhcFundRules> => {
    const out: Record<string, KfhcFundRules> = {};
    for (const f of funds) if (f.geneva_investment) out[f.geneva_investment] = fundRules(f);
    for (const l of Object.values(model?.riskLevels ?? {}).flat()) {
        if (l.kind !== 'kfhc') continue;
        const f = l.fundId ? funds.find((x) => x.code === l.fundId || x.id === l.fundId) : undefined;
        if (f) out[l.key] = { ...fundRules(f), key: l.key };
        if (l.navBufferPct && out[l.key]) out[l.key] = { ...out[l.key], navBufferPct: l.navBufferPct };
    }
    return out;
};

export const fundRules = (f: Fund): KfhcFundRules => ({
    key: f.geneva_investment ?? '',
    currency: (f.currency || 'KWD').toUpperCase(),
    nav: f.nav_per_unit ?? 0,
    ...(f.nav_date ? { navDate: toDdMmYyyy(f.nav_date.toDate()) } : {}),
    minUnits: f.min_units ?? 0,
    feePct: f.subscription_fees_percent ?? 0,
    overheadPct: f.overhead_percent ?? 0,
    minRedemption: f.minimum_redemption ?? 0,
    minHolding: f.minimum_holding ?? 0,
    ...(Number(f.multiples_of_units) > 0 ? { multipleUnits: Number(f.multiples_of_units) } : {}), // Number(): the field may have been typed in the console as a string
});

// ponytail: hard-coded top-up minimum per house fund (Geneva key) — the sukuk fund takes 1,000-unit additions once the 3,000-unit minimum holding is there. Move to funds/{id} when the rule is confirmed.
export const KFHC_ADDITIONAL_MIN_UNITS: Record<string, number> = { '500500': 1000 };

/** Fund-currency cost of one unit: the last nav plus the model's NAV buffer (the next NAV may be higher; KWD funds also carry FX), plus fees, plus overhead. */
const unitCost = (f: KfhcFundRules): number => f.nav * (1 + (f.navBufferPct ?? 0) / 100) * (1 + f.feePct / 100) * (1 + f.overheadPct / 100);

/** Same formula as the order form (components/order_info.tsx): nav × min units, plus fees, plus overhead — in the fund currency. Nav carries the model's NAV buffer.
 *  A portfolio already holding the minimum uses the fund's additional-subscription step when one is known (Global DPM only). Rounded up to the fund's multiple of units. */
export const kfhcMinSubscription = (f: KfhcFundRules, heldUnits = 0): number => {
    const step = KFHC_ADDITIONAL_MIN_UNITS[f.key];
    const units = step && heldUnits >= (f.minHolding || f.minUnits) ? step : f.minUnits;
    const m = f.multipleUnits;
    return unitCost(f) * (m ? Math.max(m, Math.ceil(units / m) * m) : units);
};

/* ---------- batch ---------- */

export interface RebalancePortfolio {
    id: string;
    riskProfile?: string;
    position?: PositionDetails;
}

export interface ComputeBatchInputs {
    portfolios: RebalancePortfolio[];
    model: Pick<GlobalDpmModel, 'riskLevels'>;
    riskIds: string[];
    funds: Record<string, KfhcFundRules>; // by geneva key
    pendingPortfolioIds: Set<string>; // portfolios with pending allocation rows outside the open batches (manual uploads, closed batches) — the whole portfolio waits
    busyKeys?: Record<string, BusyKey[]>; // per portfolio, the securities the open batches have in play — only a batch touching one of them leaves the portfolio out (see inPlay)
    thresholdPct: number;
    minTradeAmount: number;
    cashBufferPct: number;
    minCompletionPct: number;
    roundUpPct?: number; // multiples-of-units funds: buy the next step when the gap to it is at most this % of a step and the cash covers it (default BATCH_DEFAULTS.roundUpPct)
    onlyKeys?: string[]; // rebalance these model lines only (everything else holds)
    mode?: 'rebalance' | 'liquidate'; // liquidate: sell every held model line in full (onlyKeys still narrows), no buys, completion not required
    excludedPortfolioIds?: Set<string>; // left out by the team for this batch — listed with the reason, never silent
    rates: Rates;
    today: Date;
}

export interface ComputeBatchResult {
    portfolios: Record<string, GlobalDpmBatchPortfolio>;
    lines: GlobalDpmBatchLine[];
    kfhcLines: GlobalDpmBatchLine[];
    skipped: GlobalDpmBatchLine[];
    buyCandidates: GlobalDpmBatchLine[]; // every buy shortfall (kfhc included, before fallback) — what computeBuyLeg re-sizes from
}

/** "2026/09/09" → days since (fractional days truncated). NaN when unparseable. */
export const positionAgeDays = (endDate: string | undefined, today: Date): number => {
    const d = new Date(String(endDate ?? '').replace(/\//g, '-'));
    return Math.floor((today.getTime() - d.getTime()) / 86400000);
};

export interface BusyKey { key: string; by: string } // by: the batch title

/** What keeps a portfolio out of a new batch. Pending rows outside the open batches (manual uploads, closed batches) hold the whole portfolio;
 *  an open batch (draft or open) holds only its securities: every line and buy candidate of the portfolio except the lines the team excluded. */
export const inPlay = (batches: GlobalDpmBatch[], rows: Pick<GlobalDpmAllocationRow, 'status' | 'uploadId' | 'portfolioId'>[]): Pick<ComputeBatchInputs, 'pendingPortfolioIds' | 'busyKeys'> => {
    const open = batches.filter((b) => b.status !== 'done');
    const openIds = new Set(open.map((b) => b.id));
    const busyKeys: Record<string, BusyKey[]> = {};
    for (const b of open) {
        const ls = [...b.lines, ...b.kfhcLines];
        const dropped = new Set(ls.filter((l) => l.excluded).map((l) => `${l.portfolioId}|${l.key}`));
        for (const l of [...ls.filter((x) => !x.excluded), ...(b.buyCandidates ?? []).filter((c) => !dropped.has(`${c.portfolioId}|${c.key}`))]) {
            if (b.portfolios[l.portfolioId]?.excludedReason) continue;
            const mine = (busyKeys[l.portfolioId] ??= []);
            if (!mine.some((x) => x.key === l.key)) mine.push({ key: l.key, by: batchTitle(b) });
        }
    }
    return { pendingPortfolioIds: new Set(rows.filter((r) => r.status === 'pending' && !openIds.has(r.uploadId)).map((r) => r.portfolioId)), busyKeys };
};

/** The busy securities a new batch would touch: all of them for a full rebalance; with selected securities, those and their fallback chains (a house-fund buy may land on its fallback).
 *  ponytail: the other batch's fallbacks are only seen once they are lines there — a buy candidate that later falls back is not. */
const clashes = (busy: BusyKey[] | undefined, onlyKeys: string[] | undefined, model: Pick<GlobalDpmModel, 'riskLevels'>): BusyKey[] => {
    if (!busy?.length || !onlyKeys?.length) return busy ?? [];
    const touched = new Set(onlyKeys);
    const lines = Object.values(model.riskLevels).flat();
    for (const k of touched) for (const l of lines) if (l.key === k && l.fallbackKey) touched.add(l.fallbackKey); // Set iteration visits the added fallbacks too
    return busy.filter((b) => touched.has(b.key));
};

export const excludedReason = (p: RebalancePortfolio, inputs: Pick<ComputeBatchInputs, 'riskIds' | 'pendingPortfolioIds' | 'busyKeys' | 'onlyKeys' | 'today' | 'model' | 'minCompletionPct' | 'excludedPortfolioIds'>, v?: Valuation): string | undefined => {
    if (inputs.excludedPortfolioIds?.has(p.id)) return 'excluded by team';
    if (inputs.pendingPortfolioIds.has(p.id)) return 'pending allocation rows outside the open batches';
    if (!p.position) return 'no position';
    if (p.position.status !== 'Active') return `position status ${p.position.status ?? 'missing'}`;
    if (!p.riskProfile || !inputs.riskIds.includes(p.riskProfile)) return `risk profile ${p.riskProfile ?? 'missing'} is not a config risk level`;
    const age = positionAgeDays(p.position.end_date, inputs.today);
    if (!(age <= STALE_POSITION_DAYS)) return `position date ${p.position.end_date ?? 'missing'} is stale`;
    const levelLines = inputs.model.riskLevels[p.riskProfile];
    if (!levelLines?.length) return `model has no lines for ${p.riskProfile}`;
    if (v) {
        if (!(v.totalUsd > 0)) return 'portfolio value is 0';
        const c = completion(v, levelLines);
        if (c.heldPct < inputs.minCompletionPct) return `incomplete: holds ${c.held}/${c.total} model lines (${c.heldPct.toFixed(0)}% of target weight), cash ${c.cashPct.toFixed(1)}%`;
    }
    const clash = clashes(inputs.busyKeys?.[p.id], inputs.onlyKeys, inputs.model);
    if (clash.length) {
        const byBatch = new Map<string, string[]>();
        for (const b of clash) byBatch.set(b.by, [...(byBatch.get(b.by) ?? []), b.key]);
        return `in open batch ${[...byBatch].map(([by, keys]) => `${by}: ${keys.join(', ')}`).join('; ')}`;
    }
    return undefined;
};

interface DriftLine {
    line: GlobalDpmModelLine;
    holding?: HoldingLine;
    currentUsd: number;
    currentPct: number;
    targetPct: number;
    driftPct: number;
    deltaUsd: number; // target − current
    outsideLevel?: boolean; // holding of a line that belongs to another risk level → target 0
}

/** Drift of every model line of the level plus holdings outside the level (target 0). Unknown holdings (not in any level) are returned separately. */
/** The level as it applies to one portfolio: a house fund the portfolio does not hold, whose share of the value is under the fund's first-subscription minimum, moves its whole
 *  weight onto its fallback (the model says 1% money market; a 500K portfolio carries 9% sukuk instead, and sits at zero drift once it does). Held funds keep the model's target.
 *  ponytail: one hop, in line order — a fallback that is itself under its minimum is caught at buy time by sizeBuys' fall-through. */
export const effectiveLines = (levelLines: GlobalDpmModelLine[], v: Valuation, funds: Record<string, KfhcFundRules>, rates: Rates): GlobalDpmModelLine[] => {
    const target = new Map(levelLines.map((l) => [l.key, l.targetPercent]));
    const held = new Set(v.holdings.filter((h) => h.qty > 0).map((h) => lineForCode(h.code, levelLines)?.key));
    for (const l of levelLines) {
        const f = funds[l.key];
        if (l.kind !== 'kfhc' || !f || !l.fallbackKey || !target.has(l.fallbackKey) || held.has(l.key)) continue;
        const usdPerUnit = toUsd(1, f.currency, rates);
        const local = usdPerUnit > 0 ? (v.totalUsd * target.get(l.key)!) / 100 / usdPerUnit : 0;
        if (local >= kfhcMinSubscription(f)) continue;
        target.set(l.fallbackKey, target.get(l.fallbackKey)! + target.get(l.key)!);
        target.set(l.key, 0);
    }
    return levelLines.map((l) => (l.targetPercent === target.get(l.key) ? l : { ...l, targetPercent: target.get(l.key)! }));
};

export const driftLines = (v: Valuation, levelLines: GlobalDpmModelLine[], allLines: GlobalDpmModelLine[]): { drifts: DriftLine[]; unknown: HoldingLine[] } => {
    const drifts: DriftLine[] = [];
    const unknown: HoldingLine[] = [];
    const seen = new Set<string>();
    const push = (line: GlobalDpmModelLine, holding: HoldingLine | undefined, outsideLevel = false) => {
        const currentUsd = holding?.mvUsd ?? 0;
        const currentPct = v.totalUsd > 0 ? (currentUsd / v.totalUsd) * 100 : 0;
        drifts.push({ line, holding, currentUsd, currentPct, targetPct: line.targetPercent, driftPct: currentPct - line.targetPercent, deltaUsd: (v.totalUsd * line.targetPercent) / 100 - currentUsd, outsideLevel });
    };
    for (const h of v.holdings) {
        const inLevel = lineForCode(h.code, levelLines);
        if (inLevel) {
            seen.add(inLevel.key);
            push(inLevel, h);
            continue;
        }
        const elsewhere = lineForCode(h.code, allLines);
        if (elsewhere) push({ ...elsewhere, targetPercent: 0 }, h, true);
        else unknown.push(h);
    }
    for (const l of levelLines) if (!seen.has(l.key)) push(l, undefined);
    return { drifts, unknown };
};

type LineBase = Pick<GlobalDpmBatchLine, 'portfolioId' | 'key' | 'kind' | 'name' | 'securityNumber' | 'currentPct' | 'targetPct' | 'driftPct' | 'heldUnits'>;

const base = (pf: string, d: DriftLine): LineBase => ({
    portfolioId: pf,
    key: d.line.key,
    kind: d.line.kind,
    name: d.line.name || d.holding?.name || d.line.key,
    ...(d.line.securityNumber ? { securityNumber: d.line.securityNumber } : {}),
    currentPct: d.currentPct,
    targetPct: d.targetPct,
    driftPct: d.driftPct,
    ...(d.line.kind === 'kfhc' && d.holding ? { heldUnits: d.holding.qty } : {}),
});

const lineBase = (l: GlobalDpmBatchLine): LineBase => ({
    portfolioId: l.portfolioId, key: l.key, kind: l.kind, name: l.name, ...(l.securityNumber ? { securityNumber: l.securityNumber } : {}),
    currentPct: l.currentPct, targetPct: l.targetPct, driftPct: l.driftPct, ...(l.heldUnits !== undefined ? { heldUnits: l.heldUnits } : {}),
});

interface BuyCandidate {
    line: LineBase;
    shortfallUsd: number;
}

interface SizeInputs {
    funds: Record<string, KfhcFundRules>;
    rates: Rates;
    minTradeAmount: number;
    modelLines: GlobalDpmModelLine[]; // every level — fallback lookup by key
    heldUnits?: Record<string, number>; // portfolio's house-fund holdings by key — a fallback fund not among the candidates still gets its top-up minimum
    roundUpPct?: number;
}

/** Held units of a portfolio's house funds, from its drift lines. */
export const heldUnitsOf = (drifts: DriftLine[]): Record<string, number> =>
    Object.fromEntries(drifts.filter((d) => d.line.kind === 'kfhc' && d.holding).map((d) => [d.line.key, d.holding!.qty]));

/** Distribute availableUsd over the candidates pro-rata to shortfall (never more than the shortfall), then apply the kfhc minimum-subscription / fallback rule.
 *  A fallback may itself be a house fund. Fallbacks resolve after the portfolio's own buys: a fund already subscribing above its minimum just takes the extra, otherwise the
 *  fallback amount is checked against that fund's minimum on its own and falls through to its own fallback (cycles stop at a line already tried). */
export const sizeBuys = (candidates: BuyCandidate[], availableUsd: number, { funds, rates, minTradeAmount, modelLines, heldUnits = {}, roundUpPct = BATCH_DEFAULTS.roundUpPct }: SizeInputs): { lines: GlobalDpmBatchLine[]; kfhcLines: GlobalDpmBatchLine[]; skipped: GlobalDpmBatchLine[] } => {
    const lines: GlobalDpmBatchLine[] = [];
    const kfhcLines: GlobalDpmBatchLine[] = [];
    const skipped: GlobalDpmBatchLine[] = [];
    const total = candidates.reduce((s, c) => s + c.shortfallUsd, 0);
    const scale = total > 0 ? Math.min(1, Math.max(0, availableUsd) / total) : 0;
    const byKey = new Map<string, GlobalDpmBatchLine>();
    const merge = (existing: GlobalDpmBatchLine, amount: number, reason: string) => {
        const localPerUsd = existing.amountLocal !== undefined && existing.amount ? existing.amountLocal / existing.amount : undefined; // house-fund line: keep the fund-currency amount in step
        existing.amount = stripNoise((existing.amount ?? 0) + amount);
        if (localPerUsd !== undefined) existing.amountLocal = stripNoise(existing.amount * localPerUsd);
        if (!existing.reason.includes(reason)) existing.reason += `+${reason}`;
    };
    const add = (into: GlobalDpmBatchLine[], c: BuyCandidate, amount: number, reason: string, extra: Partial<GlobalDpmBatchLine> = {}) => {
        const l: GlobalDpmBatchLine = { ...c.line, phase: 'buy', side: 'BUY', amount: stripNoise(amount), shortfallUsd: c.shortfallUsd, reason, ...extra };
        byKey.set(c.line.key, l);
        into.push(l);
    };
    const deferred: [BuyCandidate, number, Set<string>][] = [];
    const place = (c: BuyCandidate, amount: number, reason: string, tried: Set<string>, defer = false): void => {
        const existing = byKey.get(c.line.key);
        if (existing) return merge(existing, amount, reason); // already buying (a house fund: above its minimum) — the extra rides along
        if (c.line.kind === 'allfunds') return add(lines, c, amount, reason);
        const f = funds[c.line.key];
        const usdPerUnit = f ? toUsd(1, f.currency, rates) : 0;
        const amountLocal = usdPerUnit > 0 ? amount / usdPerUnit : 0;
        if (f && amountLocal >= kfhcMinSubscription(f, c.line.heldUnits)) {
            return add(kfhcLines, c, amount, reason, { amountLocal: stripNoise(amountLocal), currency: f.currency, navUsed: f.nav, ...(f.navBufferPct ? { navBufferPct: f.navBufferPct } : {}), ...(f.navDate ? { navDate: f.navDate } : {}) });
        }
        tried.add(c.line.key);
        if (defer) deferred.push([c, amount, tried]);
        else fallThrough(c, amount, tried);
    };
    const fallThrough = (c: BuyCandidate, amount: number, tried: Set<string>) => {
        const f = funds[c.line.key];
        const fallbackKey = modelLines.find((m) => m.key === c.line.key)?.fallbackKey;
        const fbModel = fallbackKey && !tried.has(fallbackKey) ? modelLines.find((m) => m.key === fallbackKey) : undefined;
        if (!fbModel) {
            skipped.push({ ...c.line, phase: 'buy', side: 'BUY', amount: stripNoise(amount), shortfallUsd: c.shortfallUsd, reason: f ? 'below_min_subscription' : 'no_fund_doc' });
            return;
        }
        const fb = candidates.find((x) => x.line.key === fallbackKey)
            ?? { line: { portfolioId: c.line.portfolioId, key: fbModel.key, kind: fbModel.kind, name: fbModel.name, ...(fbModel.securityNumber ? { securityNumber: fbModel.securityNumber } : {}), currentPct: 0, targetPct: fbModel.targetPercent, driftPct: 0, ...(heldUnits[fbModel.key] !== undefined ? { heldUnits: heldUnits[fbModel.key] } : {}) }, shortfallUsd: 0 };
        place(fb, amount, f ? `fallback:${c.line.key}` : `fallback:${c.line.key}:no_fund_doc`, tried); // no_fund_doc: no funds/{id} with geneva_investment == key — fix the model key or the fund doc
    };
    for (const c of candidates) {
        const amount = c.shortfallUsd * scale;
        if (amount <= 0 || amount < minTradeAmount) {
            skipped.push({ ...c.line, phase: 'buy', side: 'BUY', amount: stripNoise(amount), shortfallUsd: c.shortfallUsd, reason: amount <= 0 ? 'no_cash' : 'below_min_trade' });
            continue;
        }
        place(c, amount, 'drift', new Set(), true);
    }
    for (const [c, amount, tried] of deferred) fallThrough(c, amount, tried);
    // a fund subscribing in multiples of units (sukuk: 1,000) takes whole steps: floored (never under the minimum — it is rounded up to the step too), then one step up
    // when the gap to the next multiple is at most roundUpPct of a step (1,860 → 2,000 at 15%; 1,840 stays 1,000) and the cash left after every buy covers it. Otherwise the remainder stays cash.
    const setUnits = (l: GlobalDpmBatchLine, f: KfhcFundRules, units: number) => {
        l.qty = units;
        l.amountLocal = stripNoise(units * unitCost(f));
        l.amount = stripNoise(toUsd(l.amountLocal, f.currency, rates));
    };
    const exact = new Map<GlobalDpmBatchLine, number>();
    for (const l of kfhcLines) {
        const f = funds[l.key];
        if (!f.multipleUnits || !(unitCost(f) > 0)) continue; // no nav yet: placed as an amount, as before
        const units = stripNoise(l.amountLocal! / unitCost(f));
        exact.set(l, units);
        setUnits(l, f, Math.floor(stripNoise(units / f.multipleUnits)) * f.multipleUnits);
    }
    let spareUsd = availableUsd - [...lines, ...kfhcLines].reduce((s, l) => s + (l.amount ?? 0), 0);
    for (const [l, units] of exact) {
        const f = funds[l.key];
        const m = f.multipleUnits!;
        const stepUsd = toUsd(m * unitCost(f), f.currency, rates);
        const past = stripNoise(units - l.qty!);
        if (!(past > 0) || stripNoise(m - past) > (m * roundUpPct) / 100 || stepUsd > spareUsd) continue;
        spareUsd -= stepUsd;
        setUnits(l, f, l.qty! + m);
        l.reason += '+round_up';
    }
    return { lines, kfhcLines, skipped };
};

/** The draft: exclusions → drift → sells (units) → buys (USD, estimated from cash + planned proceeds − buffer). */
export const computeBatch = (inputs: ComputeBatchInputs): ComputeBatchResult => {
    const { model, funds, rates, thresholdPct, minTradeAmount, cashBufferPct } = inputs;
    const allLines = Object.values(model.riskLevels).flat();
    const wholeUnitCodes = new Set(allLines.filter((l) => l.kind === 'kfhc').map((l) => l.key));
    const result: ComputeBatchResult = { portfolios: {}, lines: [], kfhcLines: [], skipped: [], buyCandidates: [] };
    const liquidate = inputs.mode === 'liquidate';

    for (const p of inputs.portfolios) {
        const v = portfolioValueUsd(p.position, rates, wholeUnitCodes);
        const snap: GlobalDpmBatchPortfolio = { riskProfile: p.riskProfile ?? '', totalValueUsd: stripNoise(v.totalUsd), cashUsd: stripNoise(v.cashUsd), positionEndDate: p.position?.end_date ?? '' };
        const reason = excludedReason(p, liquidate ? { ...inputs, minCompletionPct: 0 } : inputs, v); // an incomplete portfolio can still be liquidated
        if (reason) {
            result.portfolios[p.id] = { ...snap, excludedReason: reason };
            continue;
        }
        result.portfolios[p.id] = snap;
        const { drifts: modelDrifts, unknown } = driftLines(v, effectiveLines(model.riskLevels[p.riskProfile!], v, funds, rates), allLines);
        // liquidate: every held line becomes a target-0 sell (the sellAll path below); unheld lines drop out so nothing is bought. Unknown holdings stay in skipped.
        const drifts = liquidate ? modelDrifts.filter((d) => !!d.holding && d.holding.qty > 0).map((d) => ({ ...d, targetPct: 0, driftPct: d.currentPct, deltaUsd: -d.currentUsd })) : modelDrifts;
        for (const h of unknown) {
            result.skipped.push({ portfolioId: p.id, key: h.code, kind: 'allfunds', name: h.name, phase: 'sell', side: 'SELL', qty: h.qty, currentPct: v.totalUsd > 0 ? (h.mvUsd / v.totalUsd) * 100 : 0, targetPct: 0, driftPct: 0, reason: 'unknown_holding' });
        }

        let plannedProceeds = 0;
        const buys: BuyCandidate[] = [];
        const only = inputs.onlyKeys?.length ? new Set(inputs.onlyKeys) : null;
        for (const d of drifts) {
            if (only && !only.has(d.line.key)) continue; // single-security rebalance: everything else holds
            const b = base(p.id, d);
            const sellAll = liquidate || (d.targetPct === 0 && d.currentUsd > 0); // liquidate: even a holding valued at 0 (no price / no fx rate) is sold — its 0 proceeds are the visible signal
            if (!sellAll && Math.abs(d.driftPct) < thresholdPct) continue; // hold, not worth listing
            if (!sellAll && Math.abs(d.deltaUsd) < minTradeAmount) {
                result.skipped.push({ ...b, phase: d.deltaUsd < 0 ? 'sell' : 'buy', side: d.deltaUsd < 0 ? 'SELL' : 'BUY', reason: 'below_min_trade' });
                continue;
            }
            if (d.deltaUsd > 0) {
                buys.push({ line: b, shortfallUsd: d.deltaUsd });
                result.buyCandidates.push({ ...b, phase: 'buy', side: 'BUY', shortfallUsd: stripNoise(d.deltaUsd), reason: 'candidate' });
                continue;
            }
            const h = d.holding!;
            if (d.line.kind === 'allfunds') {
                const qty = sellAll ? h.qty : Math.min(h.qty, h.priceUsd > 0 ? -d.deltaUsd / h.priceUsd : h.qty);
                plannedProceeds += qty * h.priceUsd;
                result.lines.push({ ...b, phase: 'sell', side: 'SELL', qty: stripNoise(qty), proceedsUsd: stripNoise(qty * h.priceUsd), reason: liquidate ? 'liquidate' : d.outsideLevel ? 'not_in_level' : sellAll ? 'sell_all' : 'drift' });
                continue;
            }
            // kfhc redemption: whole units, fund minimums, min-holding policy
            const f = funds[d.line.key];
            const navUsd = f ? toUsd(f.nav, f.currency, rates) : h.priceUsd;
            const holding = Math.floor(h.qty);
            let units = sellAll ? holding : Math.min(holding, Math.floor(navUsd > 0 ? -d.deltaUsd / navUsd : 0));
            const policy = d.line.minHoldingPolicy ?? 'hold';
            let reason = liquidate ? 'liquidate' : d.outsideLevel ? 'not_in_level' : sellAll ? 'sell_all' : 'drift';
            if (f && !sellAll && holding - units > 0 && holding - units < f.minHolding) {
                if (policy === 'sell_all') { units = holding; reason = 'sell_all'; }
                else { units = Math.max(0, holding - f.minHolding); reason = 'min_holding_hold'; }
            }
            if (f && units > 0 && units < f.minRedemption) {
                result.skipped.push({ ...b, phase: 'sell', side: 'SELL', qty: units, reason: 'below_min_redemption', minHoldingPolicy: policy });
                continue;
            }
            if (units <= 0) {
                result.skipped.push({ ...b, phase: 'sell', side: 'SELL', qty: 0, reason, minHoldingPolicy: policy });
                continue;
            }
            const proceedsUsd = units * navUsd;
            plannedProceeds += proceedsUsd;
            result.kfhcLines.push({ ...b, phase: 'sell', side: 'SELL', qty: units, ...(f ? { currency: f.currency, navUsed: f.nav, amountLocal: stripNoise(units * f.nav), ...(f.navDate ? { navDate: f.navDate } : {}) } : {}), proceedsUsd: stripNoise(proceedsUsd), reason, minHoldingPolicy: policy });
        }
        const available = v.cashUsd + plannedProceeds - (v.totalUsd * cashBufferPct) / 100;
        const sized = sizeBuys(buys, available, { funds, rates, minTradeAmount, modelLines: allLines, heldUnits: heldUnitsOf(modelDrifts), roundUpPct: inputs.roundUpPct });
        result.lines.push(...sized.lines);
        result.kfhcLines.push(...sized.kfhcLines);
        result.skipped.push(...sized.skipped);
    }
    return result;
};

/* ---------- buy leg (Phase 2: the trade allocation says a sell is done, the position says how much cash there is) ---------- */

export interface LivePosition { v: Valuation; positionDate?: string } // positionDate = position.end_date ("2026/09/10:23:59:59")

/** "2026/09/10:23:59:59" → local midnight of that day (Invalid Date when unparseable — every comparison with it is false). The time is dropped: a ":" left on the day made it NaN, so no buy ever counted as inside the position. */
const positionDay = (endDate: string | undefined): Date => {
    const [y, m, d] = String(endDate ?? '').split(/[/:-]/).map(Number);
    return new Date(y, m - 1, d);
};

/** The position to value a portfolio with: today's trade_dates/{date}/portfolios copy (gateway sync ~12:30) whenever it exists, else the root doc (5 AM load). The dated copy is what the team synced for today, so it wins regardless of end_date. */
export const pickPosition = (root: PositionDetails | undefined, dated: PositionDetails | undefined): PositionDetails | undefined => dated ?? root;

type LegRow = Pick<GlobalDpmAllocationRow, 'id' | 'status'> & { allocatedAt?: { toDate(): Date } };

/** A Firestore Timestamp — or the { seconds, nanoseconds } map a JSON round-trip left on lines ticked done before a placement (fixed in place(); old docs may still carry it). */
export const stampDate = (t: unknown): Date | undefined => {
    const x = t as { toDate?: () => Date; seconds?: number } | undefined;
    return typeof x?.toDate === 'function' ? x.toDate() : typeof x?.seconds === 'number' ? new Date(x.seconds * 1000) : undefined;
};

/** A placed line is inside the position once its row is allocated (house fund: ticked done) on a day before the position's date.
 *  ponytail: strict day compare — a buy booked on the position's own day is still subtracted from cash (under-buys for ≤ 1 day, never over-buys). Upgrade path: a sync timestamp on the trade_dates doc. */
export const inPosition = (l: Pick<GlobalDpmBatchLine, 'kind' | 'done' | 'doneAt'>, row: LegRow | undefined, positionDate: string | undefined): boolean => {
    const at = stampDate(l.kind === 'allfunds' ? (row?.status === 'allocated' ? row.allocatedAt : undefined) : l.done ? l.doneAt : undefined);
    return !!at && at.getTime() < positionDay(positionDate).getTime();
};

/** The buys still to place, from the LIVE position only — no proceeds arithmetic. Holdings and targets are the position's; the only thing taken from another open batch is its buys placed but not yet inside the position, so the same cash is not spent twice.
 *  Gate: a portfolio's buys wait (returned in `waiting`) until every sell line of the batch for it is settled — allfunds: row allocated by its trade (or marked by hand), house fund: ticked done. Excluding the sell releases them.
 *  Size: shortfall per draft candidate key recomputed from the live holdings; available = live cash − buffer − buys placed but not yet inside the position (see inPosition), which also come off their key's shortfall.
 *  Sizes from buyCandidates (the shortfalls before fallback), so a house-fund subscription is re-checked against the fund minimum. Legacy drafts without buyCandidates fall back to their buy lines. */
export const computeBuyLeg = (
    batch: Pick<GlobalDpmBatch, 'portfolios' | 'lines' | 'kfhcLines' | 'cashBufferPct' | 'minTradeAmount' | 'thresholdPct'> & Partial<Pick<GlobalDpmBatch, 'buyCandidates' | 'roundUpPct'>>,
    rows: LegRow[],
    positions: Record<string, LivePosition>, // by portfolio id — what the page values the portfolio with right now
    riskLevels: Record<string, GlobalDpmModelLine[]>, // the batch's model version
    funds: Record<string, KfhcFundRules>,
    rates: Rates,
    skip: Set<string> = new Set(), // `${portfolioId}|${key}` set aside for this sizing (the place modal's Later / Exclude) — their cash goes to the others
    others: Pick<GlobalDpmBatch, 'lines' | 'kfhcLines' | 'status'>[] = [], // every other batch, closed ones too (a row allocated today is not in today's position) — rows must hold their rows
): { lines: GlobalDpmBatchLine[]; kfhcLines: GlobalDpmBatchLine[]; skipped: GlobalDpmBatchLine[]; waiting: GlobalDpmBatchLine[] } => {
    const out = { lines: [] as GlobalDpmBatchLine[], kfhcLines: [] as GlobalDpmBatchLine[], skipped: [] as GlobalDpmBatchLine[], waiting: [] as GlobalDpmBatchLine[] };
    const all = [...batch.lines, ...batch.kfhcLines];
    const allLines = Object.values(riskLevels).flat();
    const rowById = new Map(rows.map((r) => [r.id, r]));
    const rowOf = (l: GlobalDpmBatchLine) => (l.rowId ? rowById.get(l.rowId) : undefined);
    // another batch's buy counts while its order is live: an AllFunds row that still exists (deleted = cancelled); a closed batch's house-fund instruction only once ticked done (never sent otherwise)
    const othersLines = others.flatMap((o) => [...o.lines.filter((l) => !!rowOf(l)), ...o.kfhcLines.filter((l) => o.status !== 'done' || !!l.done)]);
    const excluded = new Set<string>(skip);
    for (const l of all) if (l.phase === 'buy' && l.excluded) excluded.add(`${l.portfolioId}|${l.key}`);
    const byPf = new Map<string, GlobalDpmBatchLine[]>();
    for (const l of batch.buyCandidates?.length ? batch.buyCandidates : all) {
        if (l.phase !== 'buy' || !(l.shortfallUsd! > 0) || excluded.has(`${l.portfolioId}|${l.key}`)) continue;
        (byPf.get(l.portfolioId) ?? byPf.set(l.portfolioId, []).get(l.portfolioId)!).push(l);
    }
    const size = { funds, rates, minTradeAmount: batch.minTradeAmount, modelLines: allLines, roundUpPct: batch.roundUpPct };
    for (const [pf, cands] of byPf) {
        const snap = batch.portfolios[pf];
        if (!snap || snap.excludedReason) continue;
        const live = positions[pf];
        const hold = (reason: string) => out.waiting.push(...cands.map((l): GlobalDpmBatchLine => ({ ...lineBase(l), phase: 'buy', side: 'BUY', shortfallUsd: l.shortfallUsd, reason })));
        if (!live) { hold('no_position'); continue; }
        const sells = all.filter((l) => l.phase === 'sell' && l.portfolioId === pf && !l.excluded);
        const settled = sells.filter((l) => (l.kind === 'allfunds' ? rowOf(l)?.status === 'allocated' : !!l.done)).length;
        if (settled < sells.length) { hold(`waiting_sells ${settled}/${sells.length}`); continue; }

        // placed but not yet inside the position (this batch or another open one): the cash is still there, so it must not be spent twice
        let pendingUsd = 0;
        const pendingByKey = new Map<string, number>();
        const boughtKeys = new Set<string>(); // keys this batch already placed a buy for, in the position or not
        for (const l of [...all, ...othersLines]) {
            if (l.phase !== 'buy' || l.portfolioId !== pf || l.excluded || !(l.kind === 'allfunds' ? !!l.rowId : !!l.round || !!l.done)) continue;
            if (all.includes(l)) boughtKeys.add(l.key);
            if (inPosition(l, rowOf(l), live.positionDate)) continue;
            pendingUsd += l.amount ?? 0;
            pendingByKey.set(l.key, (pendingByKey.get(l.key) ?? 0) + (l.amount ?? 0));
        }
        const { drifts } = driftLines(live.v, effectiveLines(riskLevels[snap.riskProfile] ?? [], live.v, funds, rates), allLines);
        const candidates: BuyCandidate[] = [];
        for (const c of cands) {
            const d = drifts.find((x) => x.line.key === c.key && !x.outsideLevel);
            const left = stripNoise(Math.max(0, d?.deltaUsd ?? 0) - (pendingByKey.get(c.key) ?? 0));
            if (!(left > 0)) continue; // bought already
            const line = d ? base(pf, d) : lineBase(c);
            if (live.v.totalUsd > 0 && (left / live.v.totalUsd) * 100 < batch.thresholdPct) {
                // a line bought in an earlier round whose rest (price moves, cash that ran out) is under the threshold is done — same as a hold in the draft, not listed
                if (!boughtKeys.has(c.key)) out.skipped.push({ ...line, phase: 'buy', side: 'BUY', shortfallUsd: left, reason: 'below_threshold' });
                continue;
            }
            candidates.push({ line, shortfallUsd: left });
        }
        const sized = sizeBuys(candidates, live.v.cashUsd - (live.v.totalUsd * batch.cashBufferPct) / 100 - pendingUsd, { ...size, heldUnits: heldUnitsOf(drifts) });
        out.lines.push(...sized.lines);
        out.kfhcLines.push(...sized.kfhcLines);
        out.skipped.push(...sized.skipped);
    }
    return out;
};

/** Sell lines (allfunds and kfhc) not yet placed and not excluded. */
export const remainingSells = (batch: Pick<GlobalDpmBatch, 'lines' | 'kfhcLines'>): { lines: GlobalDpmBatchLine[]; kfhcLines: GlobalDpmBatchLine[] } => ({
    lines: batch.lines.filter((l) => l.phase === 'sell' && !l.rowId && !l.excluded),
    kfhcLines: batch.kfhcLines.filter((l) => l.phase === 'sell' && !l.round && !l.excluded),
});

/* ---------- rows ---------- */

/** One AllFunds order per instrument and side: the totals the team places manually. */
export interface OrderSummary { key: string; name: string; securityNumber?: string; side: 'SELL' | 'BUY'; phase: 'sell' | 'buy'; qty: number; amount: number; estUsd: number; portfolios: number }

export const orderKey = (l: Pick<GlobalDpmBatchLine, 'key' | 'side'>) => `${l.key}|${l.side}`;

/** kind: AllFunds orders by default; 'kfhc' sums the house-fund instructions the same way (display only — they never become rows). */
export const orderSummary = (lines: GlobalDpmBatchLine[], kind: GlobalDpmBatchLine['kind'] = 'allfunds'): OrderSummary[] => {
    const m = new Map<string, OrderSummary>();
    for (const l of lines) {
        if (l.kind !== kind) continue;
        const k = orderKey(l);
        const o = m.get(k) ?? { key: l.key, name: l.name, ...(l.securityNumber ? { securityNumber: l.securityNumber } : {}), side: l.side, phase: l.phase, qty: 0, amount: 0, estUsd: 0, portfolios: 0 };
        o.qty = stripNoise(o.qty + (l.qty ?? 0));
        o.amount = stripNoise(o.amount + (l.amount ?? 0));
        o.estUsd = stripNoise(o.estUsd + (l.proceedsUsd ?? 0));
        o.portfolios += 1;
        m.set(k, o);
    }
    return [...m.values()].sort((a, b) => a.side.localeCompare(b.side) || a.name.localeCompare(b.name));
};

/** What a batch is called: its own name, else the label of the model it was computed from. */
export const batchTitle = (b: Pick<GlobalDpmBatch, 'modelLabel' | 'name'>) => b.name || b.modelLabel;

/** The lines of a round's allocation upload file: placed AllFunds lines only — house-fund instructions never go to AllFunds. Sells carry Quantity, buys Amount, never both. */
export const allocationFileRows = (batch: Pick<GlobalDpmBatch, 'lines'>, round: number) =>
    batch.lines.filter((l) => l.kind === 'allfunds' && l.round === round && !l.excluded && !!l.orderDate).map((l) => ({
        orderDate: l.orderDate!, isin: l.key, securityNumber: l.securityNumber ?? '', fundName: l.name, type: l.side === 'BUY' ? 'SUBSCRIPTION' : 'REDEMPTION', portfolioId: l.portfolioId,
        amount: l.side === 'BUY' ? l.amount ?? 0 : 0, quantity: l.side === 'SELL' ? l.qty ?? 0 : 0,
    }));

/** The allocation rows of one placement round, in the existing upload contract. Sells carry units, buys USD — never both.
 *  dates: `${key}|${side}` → dd-MM-yyyy (orders are placed per instrument on different days); lines whose order has no date are left out.
 *  The id ends in `_r{n}` so a later round on the same instrument and portfolio never overwrites an earlier row. uploadedAt is added by the caller. */
export const rowsForRound = (batch: Pick<GlobalDpmBatch, 'id' | 'modelLabel' | 'name'>, lines: GlobalDpmBatchLine[], round: number, dates: Record<string, string>, userEmail: string): Omit<GlobalDpmAllocationRow, 'uploadedAt'>[] =>
    lines.filter((l) => l.kind === 'allfunds' && dates[orderKey(l)]).map((l) => ({
        id: `${l.key}_${l.portfolioId}_${l.side}_${batch.id}_r${round}`,
        valueDate: dates[orderKey(l)],
        isin: l.key,
        securityNumber: l.securityNumber ?? '',
        fundName: l.name,
        side: l.side,
        portfolioId: l.portfolioId,
        amount: l.side === 'BUY' ? (l.amount ?? 0) : 0,
        quantity: l.side === 'SELL' ? (l.qty ?? 0) : 0,
        status: 'pending' as const,
        uploadId: batch.id,
        uploadFileName: `batch: ${batchTitle(batch)}`,
        uploadedBy: userEmail,
    }));

/** Stamp for a row the team marks allocated by hand (no AllFunds trade): sells settle their own units, buys get amount ÷ NAV units. */
export const manualAllocation = (row: Pick<GlobalDpmAllocationRow, 'side' | 'quantity' | 'amount'>, nav: number): { allocatedQty: number; allocatedNav: number } => ({
    allocatedQty: row.side === 'SELL' ? row.quantity ?? 0 : nav > 0 ? stripNoise(row.amount / nav) : 0,
    allocatedNav: nav,
});

type BasisRow = Pick<GlobalDpmAllocationRow, 'id' | 'isin' | 'side' | 'amount' | 'quantity' | 'valueDate' | 'portfolioId'>;

/** Pending rows already on the date whose basis (quantity-only vs amount-only) would make an isin|side group mixed-basis once the batch rows join it. */
export const mixedBasisConflicts = (batchRows: BasisRow[], existingPending: BasisRow[]): BasisRow[] => {
    const groups = new Map<string, BasisRow[]>();
    for (const r of batchRows) (groups.get(`${r.isin}|${r.side}`) ?? groups.set(`${r.isin}|${r.side}`, []).get(`${r.isin}|${r.side}`)!).push(r);
    const out: BasisRow[] = [];
    for (const [k, rows] of groups) {
        const others = existingPending.filter((r) => `${r.isin}|${r.side}` === k && !rows.some((b) => b.id === r.id));
        if (others.length && splitBasis([...rows, ...others]) === null) out.push(...others);
    }
    return out;
};

/* ---------- statistics ---------- */

export const AGE_BANDS = ['<30', '30–39', '40–49', '50–59', '60+', 'unknown'] as const;

export const ageBand = (birth: Date | null | undefined, today: Date): (typeof AGE_BANDS)[number] => {
    if (!birth || isNaN(birth.getTime())) return 'unknown';
    let age = today.getFullYear() - birth.getFullYear();
    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age -= 1;
    return age < 30 ? '<30' : age < 40 ? '30–39' : age < 50 ? '40–49' : age < 60 ? '50–59' : '60+';
};

/** Copy of shared-helpers parseBirthFromCivilID (the webapp cannot import shared-helpers); format only, no checksum. */
export const birthFromCivilID = (civilID: string | undefined): Date | null => {
    const s = (civilID ?? '').trim();
    if (!/^\d{12}$/.test(s)) return null;
    const century = s[0] === '2' ? 1900 : s[0] === '3' ? 2000 : null;
    if (century === null) return null;
    const d = new Date(century + Number(s.slice(1, 3)), Number(s.slice(3, 5)) - 1, Number(s.slice(5, 7)));
    return isNaN(d.getTime()) ? null : d;
};

/** Per risk level: Σ mvUsd per model key ÷ Σ total, next to the model target. */
export interface CompositeLine { key: string; name: string; kind: string; actualPct: number; targetPct: number; mvUsd: number }

export const compositeByLevel = (valuations: { riskProfile: string; v: Valuation }[], model: Pick<GlobalDpmModel, 'riskLevels'>): Record<string, CompositeLine[]> => {
    const out: Record<string, CompositeLine[]> = {};
    for (const [risk, lines] of Object.entries(model.riskLevels)) {
        const mine = valuations.filter((x) => x.riskProfile === risk);
        const total = mine.reduce((s, x) => s + x.v.totalUsd, 0);
        const mv = new Map<string, number>();
        let cash = 0;
        for (const x of mine) {
            cash += x.v.cashUsd;
            for (const h of x.v.holdings) {
                const l = lineForCode(h.code, lines);
                const k = l?.key ?? `other:${h.code}`;
                mv.set(k, (mv.get(k) ?? 0) + h.mvUsd);
            }
        }
        const rows: CompositeLine[] = lines.map((l) => ({ key: l.key, name: l.name, kind: l.kind, mvUsd: mv.get(l.key) ?? 0, actualPct: total > 0 ? ((mv.get(l.key) ?? 0) / total) * 100 : 0, targetPct: l.targetPercent }));
        for (const [k, m] of mv) if (k.startsWith('other:')) rows.push({ key: k.slice(6), name: k.slice(6), kind: 'other', mvUsd: m, actualPct: total > 0 ? (m / total) * 100 : 0, targetPct: 0 });
        rows.push({ key: 'CASH', name: 'Cash', kind: 'cash', mvUsd: cash, actualPct: total > 0 ? (cash / total) * 100 : 0, targetPct: 0 });
        out[risk] = rows;
    }
    return out;
};

/** Composite per group (customer segment): a group mixes levels, so each line's target is the levels' targets weighted by their AUM in the group. */
export const compositeByGroup = (valuations: { group: string; riskProfile: string; v: Valuation }[], model: Pick<GlobalDpmModel, 'riskLevels'>): Record<string, CompositeLine[]> => {
    const out: Record<string, CompositeLine[]> = {};
    for (const g of [...new Set(valuations.map((x) => x.group))].sort()) {
        const mine = valuations.filter((x) => x.group === g && model.riskLevels[x.riskProfile]); // same as by level: portfolios on no model level are left out
        const total = mine.reduce((s, x) => s + x.v.totalUsd, 0);
        const merged = new Map<string, CompositeLine>();
        for (const [risk, rows] of Object.entries(compositeByLevel(mine, model))) {
            const levelTotal = mine.filter((x) => x.riskProfile === risk).reduce((s, x) => s + x.v.totalUsd, 0);
            if (levelTotal <= 0) continue;
            for (const r of rows) {
                const m = merged.get(r.key) ?? { ...r, mvUsd: 0, targetPct: 0 };
                if (m.kind === 'other' && r.kind !== 'other') Object.assign(m, { name: r.name, kind: r.kind }); // a model line in another level wins over 'other'
                m.mvUsd += r.mvUsd;
                m.targetPct += (r.targetPct * levelTotal) / total;
                merged.set(r.key, m);
            }
        }
        out[g] = [...merged.values()].map((r) => ({ ...r, actualPct: total > 0 ? (r.mvUsd / total) * 100 : 0 }));
    }
    return out;
};
